// Subscription tier feature flags and limits
// New 3-tier pricing: Free Rp 0, Professional Rp 18M, Enterprise Rp 35M

export type SubscriptionTier = 'free' | 'professional' | 'enterprise' | 'enterprise_plus';

export type SupportLevel = 'community' | 'email' | 'priority' | 'dedicated_csm';
export type OnboardingLevel = 'self_service' | 'guided' | 'full_implementation' | 'custom_integration';
export type DataExportLevel = 'none' | 'pdf_csv_xls' | 'full_export' | 'realtime_api';
export type AdminDashboardLevel = 'basic' | 'full' | 'enterprise';

export interface TierLimits {
  maxWorkers: number; // -1 = unlimited
  maxSites: number;
  additionalSiteCost: number; // IDR per additional site
  aiConversationsPerMonth: number;
  maxTopupsPerMonth: number; // -1 = unlimited
  knowledgeBaseDocs: number;
  inspectionsPerMonth: number;
  features: {
    // Hazard Reporting - Granular
    hazardReporting: boolean;
    hazardReportingPhoto: boolean;
    hazardReportingVoice: boolean;
    evidenceScore: boolean;
    statusTracking: boolean;
    actionSuggestion: boolean;
    mintaBantuan: boolean;
    
    // Shift Check-in
    shiftCheckinBasic: boolean;
    fatigueScale: boolean;
    fatigueAnalysis: boolean;
    
    // Core Features
    psychometrics: boolean;
    inspections: boolean;
    accidentReport: boolean;
    
    // Supervisor Actions
    supervisorVerify: boolean;
    supervisorFix: boolean;
    
    // Analytics
    analyticsBasic: boolean;
    analyticsTrends: boolean;
    networkMetrics: boolean;
    
    // Diagnostics
    workerDiagnostics: boolean;
    workerDiagnosticsAI: boolean;
    clusterDiagnostics: boolean;
    clusterDiagnosticsAI: boolean;
    
    // Interventions
    interventionsManual: boolean;
    microlearning: boolean;
    
    // Gamification
    gamificationXP: boolean;
    gamificationBadges: boolean;
    gamificationLeaderboard: boolean;
    gamificationStreaks: boolean;
    
    // Admin
    adminDashboard: AdminDashboardLevel | false;
    aiCopilot: boolean;
    workerResults: boolean;
    
    // Data & Integration
    dataExport: boolean;
    documentUpload: boolean;
    
    // Enterprise Features
    abExperiment: boolean;
    whiteLabeling: boolean;
    apiAccess: boolean;
    
    // Incident Management
    incidentReporting: boolean;       // Basic incident form + RCA
    incidentIntelligence: boolean;    // SORIN Intelligence linking (signals, clusters, friction)
  };
  support: SupportLevel;
  onboarding: OnboardingLevel;
  dataExportFormat: DataExportLevel;
}

export const TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  free: {
    maxWorkers: -1, // Unlimited workers
    maxSites: 1,
    additionalSiteCost: 0,
    aiConversationsPerMonth: 100,
    maxTopupsPerMonth: 3,
    knowledgeBaseDocs: 0,
    inspectionsPerMonth: 0,
    features: {
      // Hazard Reporting - Text only
      hazardReporting: true,
      hazardReportingPhoto: false,
      hazardReportingVoice: false,
      evidenceScore: false,
      statusTracking: false,
      actionSuggestion: false,
      mintaBantuan: false,
      
      // Shift Check-in - Basic only
      shiftCheckinBasic: true,
      fatigueScale: false,
      fatigueAnalysis: false,
      
      // Core Features
      psychometrics: true,
      inspections: false,
      accidentReport: false,
      
      // Supervisor Actions - Verify only
      supervisorVerify: true,
      supervisorFix: false,
      
      // Analytics - Basic only
      analyticsBasic: true,
      analyticsTrends: false,
      networkMetrics: false,
      
      // Diagnostics - None
      workerDiagnostics: false,
      workerDiagnosticsAI: false,
      clusterDiagnostics: false,
      clusterDiagnosticsAI: false,
      
      // Interventions - None
      interventionsManual: false,
      microlearning: false,
      
      // Gamification - XP only
      gamificationXP: true,
      gamificationBadges: false,
      gamificationLeaderboard: false,
      gamificationStreaks: false,
      
      // Admin - None
      adminDashboard: false,
      aiCopilot: false,
      workerResults: false,
      
      // Data & Integration - None
      dataExport: false,
      documentUpload: false,
      
      // Enterprise Features - None
      abExperiment: false,
      whiteLabeling: false,
      apiAccess: false,
      
      // Incident Management - None
      incidentReporting: false,
      incidentIntelligence: false,
    },
    support: 'community',
    onboarding: 'self_service',
    dataExportFormat: 'none',
  },
  professional: {
    maxWorkers: -1, // Unlimited workers
    maxSites: 3,
    additionalSiteCost: 4000000, // Rp 4M per additional site
    aiConversationsPerMonth: 5000,
    maxTopupsPerMonth: -1, // Unlimited
    knowledgeBaseDocs: 15,
    inspectionsPerMonth: -1, // Unlimited
    features: {
      // Hazard Reporting - Complete
      hazardReporting: true,
      hazardReportingPhoto: true,
      hazardReportingVoice: true,
      evidenceScore: true,
      statusTracking: true,
      actionSuggestion: true,
      mintaBantuan: true,
      
      // Shift Check-in - Basic
      shiftCheckinBasic: true,
      fatigueScale: false,
      fatigueAnalysis: false,
      
      // Core Features
      psychometrics: true,
      inspections: true,
      accidentReport: true,
      
      // Supervisor Actions - Full
      supervisorVerify: true,
      supervisorFix: true,
      
      // Analytics - Basic + Trends
      analyticsBasic: true,
      analyticsTrends: true,
      networkMetrics: true,
      
      // Diagnostics - Without AI
      workerDiagnostics: true,
      workerDiagnosticsAI: false,
      clusterDiagnostics: true,
      clusterDiagnosticsAI: false,
      
      // Interventions - Manual only
      interventionsManual: true,
      microlearning: false,
      
      // Gamification - Full except streaks
      gamificationXP: true,
      gamificationBadges: true,
      gamificationLeaderboard: true,
      gamificationStreaks: true,
      
      // Admin - Full
      adminDashboard: 'full',
      aiCopilot: false,
      workerResults: false,
      
      // Data & Integration
      dataExport: true,
      documentUpload: true,
      
      // Enterprise Features - None
      abExperiment: false,
      whiteLabeling: false,
      apiAccess: false,
      
      // Incident Management - Professional tier
      incidentReporting: true,
      incidentIntelligence: false,    // Enterprise only
    },
    support: 'email',
    onboarding: 'guided',
    dataExportFormat: 'pdf_csv_xls',
  },
  enterprise: {
    maxWorkers: -1, // Unlimited workers
    maxSites: 6,
    additionalSiteCost: 6000000, // Rp 6M per additional site
    aiConversationsPerMonth: 25000,
    maxTopupsPerMonth: -1, // Unlimited
    knowledgeBaseDocs: 50,
    inspectionsPerMonth: -1, // Unlimited
    features: {
      // Hazard Reporting - Complete
      hazardReporting: true,
      hazardReportingPhoto: true,
      hazardReportingVoice: true,
      evidenceScore: true,
      statusTracking: true,
      actionSuggestion: true,
      mintaBantuan: true,
      
      // Shift Check-in - Full with fatigue
      shiftCheckinBasic: true,
      fatigueScale: true,
      fatigueAnalysis: true,
      
      // Core Features
      psychometrics: true,
      inspections: true,
      accidentReport: true,
      
      // Supervisor Actions - Full
      supervisorVerify: true,
      supervisorFix: true,
      
      // Analytics - Full
      analyticsBasic: true,
      analyticsTrends: true,
      networkMetrics: true,
      
      // Diagnostics - With AI
      workerDiagnostics: true,
      workerDiagnosticsAI: true,
      clusterDiagnostics: true,
      clusterDiagnosticsAI: true,
      
      // Interventions - Full with microlearning
      interventionsManual: true,
      microlearning: true,
      
      // Gamification - Full
      gamificationXP: true,
      gamificationBadges: true,
      gamificationLeaderboard: true,
      gamificationStreaks: true,
      
      // Admin - Enterprise
      adminDashboard: 'enterprise',
      aiCopilot: true,
      workerResults: true,
      
      // Data & Integration
      dataExport: true,
      documentUpload: true,
      
      // Enterprise Features - Some
      abExperiment: true,
      whiteLabeling: false,
      apiAccess: false,
      
      // Incident Management - Full SORIN Intelligence
      incidentReporting: true,
      incidentIntelligence: true,
    },
    support: 'priority',
    onboarding: 'full_implementation',
    dataExportFormat: 'full_export',
  },
  enterprise_plus: {
    maxWorkers: -1,
    maxSites: -1, // Unlimited
    additionalSiteCost: 0,
    aiConversationsPerMonth: -1, // Unlimited
    maxTopupsPerMonth: -1,
    knowledgeBaseDocs: -1, // Unlimited
    inspectionsPerMonth: -1,
    features: {
      // All features enabled
      hazardReporting: true,
      hazardReportingPhoto: true,
      hazardReportingVoice: true,
      evidenceScore: true,
      statusTracking: true,
      actionSuggestion: true,
      mintaBantuan: true,
      shiftCheckinBasic: true,
      fatigueScale: true,
      fatigueAnalysis: true,
      psychometrics: true,
      inspections: true,
      accidentReport: true,
      supervisorVerify: true,
      supervisorFix: true,
      analyticsBasic: true,
      analyticsTrends: true,
      networkMetrics: true,
      workerDiagnostics: true,
      workerDiagnosticsAI: true,
      clusterDiagnostics: true,
      clusterDiagnosticsAI: true,
      interventionsManual: true,
      microlearning: true,
      gamificationXP: true,
      gamificationBadges: true,
      gamificationLeaderboard: true,
      gamificationStreaks: true,
      adminDashboard: 'enterprise',
      aiCopilot: true,
      workerResults: true,
      dataExport: true,
      documentUpload: true,
      abExperiment: true,
      whiteLabeling: true,
      apiAccess: true,
      incidentReporting: true,
      incidentIntelligence: true,
    },
    support: 'dedicated_csm',
    onboarding: 'custom_integration',
    dataExportFormat: 'realtime_api',
  },
};

export const TIER_PRICING: Record<SubscriptionTier, { monthly: number; annual: number; usdEquivalent: string }> = {
  free: { monthly: 0, annual: 0, usdEquivalent: 'Free' },
  professional: { monthly: 18000000, annual: 18000000 * 12 * 0.85, usdEquivalent: '~$1,150' },
  enterprise: { monthly: 35000000, annual: 35000000 * 12 * 0.85, usdEquivalent: '~$2,200' },
  enterprise_plus: { monthly: 0, annual: 0, usdEquivalent: 'Custom' },
};

export const TIER_NAMES: Record<SubscriptionTier, string> = {
  free: 'Free',
  professional: 'Professional',
  enterprise: 'Enterprise',
  enterprise_plus: 'Enterprise+',
};

export const SUPPORT_LABELS: Record<SupportLevel, string> = {
  community: 'Community Forum',
  email: 'Email Support',
  priority: 'Priority Support + CSM',
  dedicated_csm: 'Dedicated CSM',
};

export const ONBOARDING_LABELS: Record<OnboardingLevel, string> = {
  self_service: 'Self-service',
  guided: 'Guided Onboarding',
  full_implementation: 'Full Implementation',
  custom_integration: 'Custom Integration',
};

export const DATA_EXPORT_LABELS: Record<DataExportLevel, string> = {
  none: 'Not Available',
  pdf_csv_xls: 'PDF, CSV, XLS',
  full_export: 'Full Export',
  realtime_api: 'Real-time API',
};

// Top-up pricing
export const TOPUP_PRICING = {
  per100Conversations: 200000, // Rp 200,000 per 100 conversations
  minimumAmount: 100,
  incrementAmount: 100,
};

// Feature labels for display
export const FEATURE_LABELS: Record<keyof TierLimits['features'], string> = {
  hazardReporting: 'Hazard Reporting',
  hazardReportingPhoto: 'Photo Attachment',
  hazardReportingVoice: 'Voice Input',
  evidenceScore: 'Evidence Quality Score',
  statusTracking: 'Status Tracking',
  actionSuggestion: 'Action Suggestions',
  mintaBantuan: 'Minta Bantuan (Request Help)',
  shiftCheckinBasic: 'Shift Check-in',
  fatigueScale: 'Fatigue Scale',
  fatigueAnalysis: 'Fatigue Analysis',
  psychometrics: 'Psychometric Assessment',
  inspections: 'AI Inspections',
  accidentReport: 'Accident Reporting',
  supervisorVerify: 'Supervisor Verification',
  supervisorFix: 'Supervisor Fix Actions',
  analyticsBasic: 'Basic Analytics',
  analyticsTrends: 'Trend Analysis',
  networkMetrics: 'Network Metrics',
  workerDiagnostics: 'Worker Diagnostics',
  workerDiagnosticsAI: 'AI Worker Recommendations',
  clusterDiagnostics: 'Cluster Diagnostics',
  clusterDiagnosticsAI: 'AI Cluster Recommendations',
  interventionsManual: 'Manual Interventions',
  microlearning: 'Micro-learning Modules',
  gamificationXP: 'XP Points',
  gamificationBadges: 'Badges',
  gamificationLeaderboard: 'Leaderboard',
  gamificationStreaks: 'Streaks',
  adminDashboard: 'Admin Dashboard',
  aiCopilot: 'AI Safety Copilot',
  workerResults: 'Worker Results Dashboard',
  dataExport: 'Data Export',
  documentUpload: 'Document Upload',
  abExperiment: 'A/B Experiments',
  whiteLabeling: 'White Labeling',
  apiAccess: 'API Access',
  incidentReporting: 'Incident Reporting & RCA',
  incidentIntelligence: 'SORIN Intelligence Linking',
};

export function getTierLimits(tier: string): TierLimits {
  return TIER_LIMITS[tier as SubscriptionTier] || TIER_LIMITS.free;
}

export function hasFeature(tier: string, feature: keyof TierLimits['features']): boolean {
  const limits = getTierLimits(tier);
  const value = limits.features[feature];
  // Handle AdminDashboardLevel type
  if (feature === 'adminDashboard') {
    return value !== false;
  }
  return value === true;
}

export function canAddWorker(tier: string, currentCount: number): boolean {
  const limits = getTierLimits(tier);
  if (limits.maxWorkers === -1) return true;
  return currentCount < limits.maxWorkers;
}

export function canAddSite(tier: string, currentCount: number): boolean {
  const limits = getTierLimits(tier);
  if (limits.maxSites === -1) return true;
  return currentCount < limits.maxSites;
}

export function getAIConversationLimit(tier: string): number {
  const limits = getTierLimits(tier);
  return limits.aiConversationsPerMonth;
}

export function getKnowledgeBaseLimit(tier: string): number {
  const limits = getTierLimits(tier);
  return limits.knowledgeBaseDocs;
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function getAdditionalSiteCost(tier: string): number {
  const limits = getTierLimits(tier);
  return limits.additionalSiteCost;
}

export function getMaxTopupsPerMonth(tier: string): number {
  const limits = getTierLimits(tier);
  return limits.maxTopupsPerMonth;
}

// Tier level comparison utility - single source of truth for tier access checks
const TIER_LEVELS: Record<string, number> = {
  free: 1,
  professional: 2,
  enterprise: 3,
  enterprise_plus: 4,
};

export function hasTierAccess(currentTier: string | undefined, requiredTier: SubscriptionTier): boolean {
  const current = TIER_LEVELS[currentTier || 'free'] || 1;
  const required = TIER_LEVELS[requiredTier] || 1;
  return current >= required;
}

export function getTierLevel(tier: string | undefined): number {
  return TIER_LEVELS[tier || 'free'] || 1;
}
